"use client";

import React, { createContext, useContext, useState } from "react";

const AccordionContext = createContext({
  activeKey: null,
  onToggle: () => {},
});

/**
 * Accordion - Collapsible content panels.
 *
 * @param {string|number} defaultActiveKey - Initially open panel key
 * @param {string|number} activeKey - Controlled active key
 * @param {function} onSelect - Called when active key changes
 */
function AccordionComponent({
  children,
  defaultActiveKey = null,
  activeKey: controlledKey,
  onSelect,
  className = "",
  ...props
}) {
  const [internalKey, setInternalKey] = useState(defaultActiveKey);
  const activeKey = controlledKey !== undefined ? controlledKey : internalKey;

  const onToggle = (key) => {
    const newKey = String(activeKey) === String(key) ? null : key;
    if (onSelect) onSelect(newKey);
    if (controlledKey === undefined) setInternalKey(newKey);
  };

  return (
    <AccordionContext.Provider value={{ activeKey, onToggle }}>
      <div className={className} {...props}>
        {children}
      </div>
    </AccordionContext.Provider>
  );
}

/**
 * Accordion.Toggle - Clickable header that toggles a panel.
 *
 * @param {React.ElementType} as - Component to render as 
 * @param {string|number} eventKey - Key of the panel to toggle
 */
function AccordionToggle({
  children,
  as: Component = "button",
  eventKey,
  className = "",
  onClick,
  ...props
}) {
  const { onToggle } = useContext(AccordionContext);

  const handleClick = (e) => {
    onToggle(eventKey);
    if (onClick) onClick(e);
  };

  return (
    <Component
      className={`cursor-pointer ${className}`.trim()}
      onClick={handleClick}
      {...props}
    >
      {children}
    </Component>
  );
}

/**
 * Accordion.Collapse - Content that shows/hides based on active key.
 *
 * @param {string|number} eventKey - Key to match against active key
 */
function AccordionCollapse({ children, eventKey, className = "", ...props }) {
  const { activeKey } = useContext(AccordionContext);
  const isOpen = String(activeKey) === String(eventKey);

  if (!isOpen) return null;

  return (
    <div className={className} {...props}>
      {children}
    </div>
  );
}

export const Accordion = Object.assign(AccordionComponent, {
  Toggle: AccordionToggle,
  Collapse: AccordionCollapse,
});
